import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  PrecontactCandidatesFilters,
  PrecontactMarkSentRequest,
  PrecontactCandidatesResponse,
  PrecontactLanguage,
  PrecontactPhoneResult,
  PrecontactPreview,
  PrecontactSendRequest,
  PrecontactSendResult,
  PrecontactSentResponse,
  PrecontactVariant,
} from '@guilleumes/types'

import { api } from '@/services/api'

/**
 * Llista de reserves d'OTA a les quals cal demanar el telèfon.
 * `plans/expedia-precontact.md` §F3 — només lectura: aquí no s'envia res.
 */
export function usePrecontactCandidates(filters: PrecontactCandidatesFilters = {}) {
  return useQuery<PrecontactCandidatesResponse>({
    queryKey: ['precontact', 'candidates', filters],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (filters.windowDays !== undefined) params.windowDays = String(filters.windowDays)
      if (filters.channels?.length) params.channels = filters.channels.join(',')
      if (filters.variant) params.variant = filters.variant
      if (filters.includeHandled) params.includeHandled = 'true'

      const response = await api.get('/precontact/candidates', { params })
      return response.data
    },
    // La finestra es mou amb el rellotge i les entrades manuals arriben per sync (§1),
    // però tampoc cal rellegir-ho cada pocs segons: és una llista de treball.
    staleTime: 60 * 1000,
  })
}

/**
 * Text exacte que rebria l'hoste amb l'idioma i la variant triats (§F5).
 *
 * Es demana al servidor i no es compon al client a posta: el text que es revisa al
 * diàleg ha de ser byte a byte el que sortirà, i qui el sap renderitzar és el
 * servidor (plantilla de la DB, remitent del JWT, data en l'idioma del text).
 */
export function usePrecontactPreview(
  reservationId: string | null,
  language: PrecontactLanguage,
  variant?: PrecontactVariant,
) {
  return useQuery<PrecontactPreview>({
    queryKey: ['precontact', 'preview', reservationId, language, variant],
    queryFn: async () => {
      const params: Record<string, string> = { language }
      if (variant) params.variant = variant

      const response = await api.get(`/precontact/${reservationId}/preview`, { params })
      return response.data
    },
    enabled: reservationId !== null,
    // El text no canvia mentre el diàleg és obert; el que el fa canviar és l'idioma,
    // i això ja és una altra queryKey.
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Enviament per fila (§F5).
 *
 * Un `POST` per reserva, sempre amb confirmació prèvia de la persona. El servidor
 * torna a comprovar totes les guardes: aquí no hi ha cap validació que hi confiï.
 */
export function useSendPrecontact() {
  const queryClient = useQueryClient()

  return useMutation<
    PrecontactSendResult,
    Error,
    { reservationId: string } & PrecontactSendRequest
  >({
    mutationFn: async ({ reservationId, ...body }) => {
      const response = await api.post(`/precontact/${reservationId}/send`, body)
      return response.data
    },
    onSuccess: () => {
      // Tant si s'ha enviat com si ha quedat SKIPPED o FAILED, la fila canvia d'estat.
      queryClient.invalidateQueries({ queryKey: ['precontact'] })
    },
  })
}

/**
 * Marca un precontacte com enviat a mà des del Message Center d'EPC (§F10.5).
 *
 * No envia res: la persona ha escrit a EPC i això només ho registra. El text no hi
 * viatja i **tampoc no el renderitza el servidor**: el llegeix del fil de Beds24
 * (§F10.7), amb el seu id i la seva hora. El que hi ha al fil és el que l'hoste ha
 * rebut de debò, i és l'única cosa que el log pot dir sense mentir.
 */
export function useMarkPrecontactSentManual() {
  const queryClient = useQueryClient()

  return useMutation<
    PrecontactSendResult,
    Error,
    { reservationId: string } & PrecontactMarkSentRequest
  >({
    mutationFn: async ({ reservationId, ...body }) => {
      const response = await api.post(`/precontact/${reservationId}/mark-sent-manual`, body)
      return response.data
    },
    onSuccess: () => {
      // La fila passa de "rebutjada" a "enviada": es mou entre les dues llistes.
      queryClient.invalidateQueries({ queryKey: ['precontact'] })
    },
  })
}

/**
 * Precontactes ja enviats, amb el que ha contestat l'hoste (§F6.3).
 *
 * Mateix endpoint que la llista de candidates, amb `view=sent`: és la mateixa
 * pàgina mirada des de l'altre costat. Es refresca més sovint perquè el que la
 * mou és un cron de 30 minuts i el que s'hi espera és una resposta.
 */
export function usePrecontactSent(enabled = true) {
  return useQuery<PrecontactSentResponse>({
    queryKey: ['precontact', 'sent'],
    queryFn: async () => {
      const response = await api.get('/precontact/candidates', { params: { view: 'sent' } })
      return response.data
    },
    enabled,
    staleTime: 60 * 1000,
  })
}

/**
 * Desa el telèfon que una persona ha confirmat de la resposta (§F6.3).
 *
 * El servidor l'escriu PRIMER a Beds24 i només llavors a la fitxa; si Beds24 falla,
 * no es desa res. Aquí no hi ha cap validació que hi confiï: el número surt d'un
 * chip que ha extret el servidor i el torna a validar el servidor.
 */
export function useSavePrecontactPhone() {
  const queryClient = useQueryClient()

  return useMutation<PrecontactPhoneResult, Error, { reservationId: string; phone: string }>({
    mutationFn: async ({ reservationId, phone }) => {
      const response = await api.patch(`/precontact/${reservationId}/phone`, { phone })
      return response.data
    },
    onSuccess: () => {
      // La fila surt de la vista `sent` i el comptador de candidates pot moure's.
      queryClient.invalidateQueries({ queryKey: ['precontact'] })
    },
  })
}

/** Comptador per al menú lateral. No renderitza cap plantilla al servidor. */
export function usePrecontactCount() {
  const { data } = useQuery<{ count: number }>({
    queryKey: ['precontact', 'count'],
    queryFn: async () => {
      const response = await api.get('/precontact/candidates/count')
      return response.data
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })

  return data?.count ?? 0
}
